import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { ArrowLeft, Check } from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { INDUSTRY_OPTIONS } from '../../utils/industrySchemas'
import ClientPermissions from './ClientPermissions'

function Field({ label, hint, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

function CardSection({ title, desc, right, children }) {
  return (
    <div className="card card-pad">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1 }}>
          <h2 className="h2">{title}</h2>
          {desc && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>{desc}</div>}
        </div>
        {right}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  )
}

// Prefix input (https:// + url)
function PrefixInput({ prefix, register: reg, name, placeholder, defaultValue }) {
  return (
    <div style={{ display: 'flex' }}>
      <span style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRight: 0, borderRadius: '10px 0 0 10px', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {prefix}
      </span>
      <input
        className="input"
        placeholder={placeholder}
        style={{ borderRadius: '0 10px 10px 0' }}
        {...reg(name)}
        defaultValue={defaultValue}
      />
    </div>
  )
}

const EditClient = () => {
  const navigate = useNavigate()
  const { clientId } = useParams()
  const queryClient = useQueryClient()

  const { data: client, isLoading: clientLoading } = useQuery(
    ['client', clientId],
    () => apiService.get(`/clients/profile?clientId=${clientId}`).then(res => res.data),
    { enabled: !!clientId }
  )

  const { data: billingPlans } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data),
    { refetchOnMount: 'always', staleTime: 0 }
  )

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm()
  const subscriptionPlan = watch('subscription_plan')

  useEffect(() => {
    if (client) {
      reset({
        company_name: client.company_name || '',
        name: client.user_name || '',
        email: client.email || '',
        phone: client.phone || '',
        domain: client.domain ? client.domain.replace(/^https?:\/\//, '') : '',
        industry: client.industry || 'general',
        subscription_plan: client.subscription_plan || 'basic',
        edit_quota: client.edit_quota || client.monthly_edit_limit || 50,
        notes: client.notes || '',
      })
    }
  }, [client, reset])

  const updateClientMutation = useMutation(
    (data) => apiService.put('/clients/profile', { ...data, clientId }),
    {
      onSuccess: () => {
        toast.success('Client updated successfully!')
        queryClient.invalidateQueries(['client', clientId])
        queryClient.invalidateQueries('clients')
        navigate(`/admin/clients/${clientId}`)
      },
      onError: (error) => toast.error(
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.msg ||
        'Failed to update client'
      )
    }
  )

  const onSubmit = (data) => {
    updateClientMutation.mutate({
      company_name: data.company_name,
      subscription_plan: data.subscription_plan,
      edit_quota: parseInt(data.edit_quota),
      industry: data.industry || 'general',
      phone: data.phone || '',
      domain: data.domain ? `https://${data.domain.replace(/^https?:\/\//, '')}` : '',
      notes: data.notes || '',
    })
  }

  const planOptions = billingPlans
    ? Object.entries(billingPlans).map(([id, plan]) => ({ id, name: plan.name, price: plan.price, quota: plan.features.editsPerMonth, custom: !!plan.custom }))
    : []

  if (clientLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256 }}>
        <LoadingSpinner size="lg" text="Loading client…" />
      </div>
    )
  }

  if (!client) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <h3 style={{ marginBottom: 8 }}>Client not found</h3>
        <button className="btn btn-primary" onClick={() => navigate('/admin/clients')}>Back to Clients</button>
      </div>
    )
  }

  const actions = (
    <>
      <button type="button" className="btn" onClick={() => navigate(`/admin/clients/${clientId}`)}>Cancel</button>
      <button type="button" className="btn btn-primary" onClick={handleSubmit(onSubmit)} disabled={updateClientMutation.isLoading}>
        {updateClientMutation.isLoading ? <LoadingSpinner size="sm" /> : <><Check style={{ width: 14, height: 14 }} /> Save changes</>}
      </button>
    </>
  )

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start', marginBottom: 4 }} onClick={() => navigate(`/admin/clients/${clientId}`)}>
            <ArrowLeft style={{ width: 14, height: 14 }} /> Back
          </button>
          <div className="eyebrow">Clients · Edit</div>
          <h1>Edit client</h1>
          <div className="subtitle">
            {client.company_name} · client since{' '}
            {client.created_at ? new Date(client.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '—'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>
      </div>

      <div className="page-body" style={{ maxWidth: 880 }}>
        <CardSection title="Contact &amp; company" desc="Who you'll be working with and how to reach them.">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Contact name">
              <input
                className="input"
                placeholder="Full name"
                {...register('name')}
                readOnly
                style={{ color: 'var(--text-muted)', cursor: 'not-allowed' }}
              />
            </Field>
            <Field label="Email" hint="managed by account — read only">
              <input
                className="input"
                defaultValue={client.email}
                readOnly
                style={{ color: 'var(--text-muted)', cursor: 'not-allowed' }}
              />
            </Field>
            <Field label="Company name">
              <input
                className="input"
                placeholder="Company"
                {...register('company_name', { required: 'Company name is required' })}
                style={errors.company_name ? { borderColor: 'var(--danger)' } : {}}
              />
              {errors.company_name && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{errors.company_name.message}</span>}
            </Field>
            <Field label="Industry">
              <select className="input" {...register('industry')}>
                {INDUSTRY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Phone" hint="optional">
              <input
                className="input"
                placeholder="+44 7700 900000"
                {...register('phone')}
              />
            </Field>
            <Field label="Website" hint="optional">
              <div style={{ display: 'flex' }}>
                <span style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRight: 0, borderRadius: '10px 0 0 10px', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  https://
                </span>
                <input
                  className="input"
                  placeholder="company.com"
                  style={{ borderRadius: '0 10px 10px 0' }}
                  {...register('domain')}
                />
              </div>
            </Field>
          </div>
        </CardSection>

        <CardSection title="Plan &amp; billing" desc="Set the package and monthly edit allowance.">
          {planOptions.length === 0 ? (
            <div style={{ padding: '16px 0' }}><LoadingSpinner size="sm" text="Loading plans…" /></div>
          ) : (
            <>
              <Field label="Plan" full>
                <div className="segmented" style={{ width: '100%' }}>
                  {planOptions.map(plan => (
                    <button
                      key={plan.id}
                      type="button"
                      className={subscriptionPlan === plan.id ? 'on' : ''}
                      style={{ flex: 1, justifyContent: 'center', padding: '8px 9px' }}
                      onClick={() => setValue('subscription_plan', plan.id)}
                    >
                      {plan.name}
                      {plan.custom && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--accent-3, var(--accent))', opacity: 0.9 }}>
                          Custom
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {planOptions.find(p => p.id === subscriptionPlan) && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    £{planOptions.find(p => p.id === subscriptionPlan).price}/month ·{' '}
                    {planOptions.find(p => p.id === subscriptionPlan).quota === 'unlimited' ? 'Unlimited' : planOptions.find(p => p.id === subscriptionPlan).quota} edits included
                  </div>
                )}
              </Field>
              <Field label="Edit quota" hint="requests included per month">
                <input
                  className="input"
                  type="number"
                  min="1"
                  max="1000"
                  {...register('edit_quota', { required: true, min: 1 })}
                />
              </Field>
            </>
          )}
        </CardSection>

        <CardSection title="Internal notes" desc="Only your team can see this.">
          <Field label="Notes" full>
            <textarea
              className="input"
              rows={4}
              style={{ resize: 'vertical', lineHeight: 1.6 }}
              placeholder="Add context for your team — preferences, history, special arrangements…"
              {...register('notes')}
            />
          </Field>
        </CardSection>

        <ClientPermissions clientId={clientId} />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingBottom: 8 }}>
          {actions}
        </div>
      </div>
    </>
  )
}

export default EditClient
